import React from 'react';
import { reportAppError } from '../utils/errorReporter';

// A ChunkLoadError means the browser has an outdated main bundle referencing
// chunk hashes that no longer exist after a redeploy. Clear the SW static cache
// (which serves the stale main.js) and hard-reload once so the new hashes load.
const CHUNK_RELOAD_KEY = 'ccc_chunk_reload_at';
const CHUNK_RELOAD_WINDOW_MS = 60 * 1000;

function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  const msg = String(error.message || '');
  return /Loading (CSS )?chunk [\d]+ failed/i.test(msg) || /Failed to fetch dynamically imported module/i.test(msg);
}

async function recoverFromChunkError() {
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      // Only wipe caches that hold the app shell / static JS/CSS chunks.
      await Promise.all(
        keys.filter((k) => k.startsWith('ccc-static-') || k.startsWith('ccc-shell-')).map((k) => caches.delete(k))
      );
    }
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage('SKIP_WAITING');
    }
  } catch (e) {
    // Best-effort cleanup; fall through to reload regardless.
  }
  // Cache-bust the navigation itself so the SW navigation-preload can't return
  // the old index.html either.
  const url = new URL(window.location.href);
  url.searchParams.set('_r', Date.now().toString(36));
  window.location.replace(url.toString());
}

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);

    // Auto-recover from post-deploy stale-bundle crashes.
    if (isChunkLoadError(error)) {
      const lastReloadAt = parseInt(sessionStorage.getItem(CHUNK_RELOAD_KEY) || '0', 10);
      const now = Date.now();
      // Avoid reload loops: only attempt one recovery per minute per session.
      if (!lastReloadAt || now - lastReloadAt > CHUNK_RELOAD_WINDOW_MS) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
        // Still log so we know how often this happens.
        reportAppError('chunk_load_reload', error?.message || String(error), {
          component_stack: (errorInfo?.componentStack || '').slice(0, 1000),
          url: window.location.href,
        }, error?.stack);
        recoverFromChunkError();
        return;
      }
    }

    reportAppError('react_render_crash', error?.message || String(error), {
      component_stack: (errorInfo?.componentStack || '').slice(0, 1000),
    }, error?.stack);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  handleLogin = () => {
    // Don't clear token — rendering errors shouldn't log users out
    window.location.href = '/auth';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 50%, #f5f3ff 100%)' }}>
          <div style={{
            background: 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(12px)',
            borderRadius: '24px',
            padding: '48px',
            maxWidth: '480px',
            width: '100%',
            textAlign: 'center',
            border: '1px solid rgba(255,255,255,0.5)',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto' }}>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h1 style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '24px', color: '#1e293b', marginBottom: '8px' }}>
              Something went wrong
            </h1>
            <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px', lineHeight: '1.6' }}>
              We hit an unexpected error. This can sometimes happen with browser caching. Try reloading or clearing your session.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={this.handleReload}
                data-testid="error-reload-btn"
                style={{
                  background: '#8b5cf6', color: 'white', border: 'none', borderRadius: '12px',
                  padding: '12px 24px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                }}
              >
                Reload App
              </button>
              <button
                onClick={this.handleLogin}
                data-testid="error-login-btn"
                style={{
                  background: 'rgba(139,92,246,0.1)', color: '#8b5cf6', border: '1px solid rgba(139,92,246,0.3)',
                  borderRadius: '12px', padding: '12px 24px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                }}
              >
                Back to Login
              </button>
            </div>
            {this.state.error && (
              <details style={{ marginTop: '24px', textAlign: 'left' }}>
                <summary style={{ color: '#94a3b8', fontSize: '12px', cursor: 'pointer' }}>Technical Details</summary>
                <pre style={{
                  background: '#f1f5f9', borderRadius: '8px', padding: '12px', marginTop: '8px',
                  fontSize: '11px', color: '#475569', overflow: 'auto', maxHeight: '120px', whiteSpace: 'pre-wrap',
                }}>
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
